// frontend/src/components/canvasser/calls/CallRow.tsx
import { Button, HStack, Text, VStack } from '@chakra-ui/react'
import { Card } from '../../shared/Card'
import { CallButton } from '../../shared/CallButton'
import { StatusChip, type StatusVariant } from '../../shared/StatusChip'
import { CALL_OUTCOME_LABELS, type CallBatchItemRowDto } from '../../../types/followUp'
import type { PendingCall } from '../../../lib/offlineStore'
import type { LocalCall } from '../../../hooks/useCanvasserCalls'

/** The row's status chip: DNC wins, then the (local, else server) call result, then a closed item. */
export function callRowChip(row: CallBatchItemRowDto, pending: PendingCall | undefined): { label: string; variant: StatusVariant } | null {
  if (row.doNotContact) return { label: 'Do not contact', variant: 'danger' }
  const outcome = pending && !pending.rejected ? pending.call.outcome : row.lastOutcome
  if (outcome) return { label: `Called · ${CALL_OUTCOME_LABELS[outcome]}`, variant: row.status === 'Open' ? 'info' : 'success' }
  if (row.status !== 'Open') return { label: 'Closed', variant: 'neutral' }
  return null
}

/** Open, callable, and not already recorded on this phone. */
export function isMarkable(row: CallBatchItemRowDto, pending: PendingCall | undefined): boolean {
  return row.status === 'Open' && !row.doNotContact && !(pending && !pending.rejected)
}

/**
 * [CF-1 P4 §7.6] One person on the call list: name, why they're on it, the organizer's note, the last
 * result, the call button (the one place a call is placed) and "Mark outcome". No copy/share affordance.
 */
export function CallRow({ row, local, canMark = true, onMarkOutcome }: {
  /** false while the screen's post-mount read is in flight (the row may be pre-drain). */
  row: CallBatchItemRowDto; local: LocalCall | undefined; canMark?: boolean; onMarkOutcome: () => void
}) {
  const pending = local?.entry
  const chip = callRowChip(row, pending)
  return (
    <Card data-testid={`call-row-${row.itemId}`} p={4} w="100%">
      <VStack align="stretch" gap={2}>
        <HStack justify="space-between" align="start" gap={2}>
          <Text fontWeight="semibold" fontSize="lg" minW={0}>{row.displayName}</Text>
          {chip && <StatusChip variant={chip.variant}>{chip.label}</StatusChip>}
        </HStack>
        <Text>{row.reason}</Text>
        {row.note && <Text fontSize="sm" color="fg.muted">{row.note}</Text>}
        {row.lastOutcome && row.attemptCount > 1 && (
          <Text fontSize="sm" color="fg.muted">{`Last: ${CALL_OUTCOME_LABELS[row.lastOutcome]} · ${row.attemptCount} calls`}</Text>
        )}
        {pending && !pending.rejected && !local?.synced && <Text fontSize="sm" color="info.fg">Saved on this phone, will sync</Text>}
        {pending?.rejected && (
          <Text fontSize="sm" color="danger.fg">{`Couldn't be saved: ${pending.rejectedReason ?? 'the server refused it'}`}</Text>
        )}
        <HStack gap={2} flexWrap="wrap">
          {/* A closed item (organizer-closed, Declined, ...) is not dialed from the list. */}
          <CallButton size="lg" phone={row.phone}
            disabledReason={row.doNotContact ? 'Do not contact' : row.status !== 'Open' ? 'Closed' : undefined} />
          {canMark && isMarkable(row, pending) && (
            <Button minH="48px" variant="outline" borderColor="border" onClick={onMarkOutcome}>Mark outcome</Button>
          )}
        </HStack>
      </VStack>
    </Card>
  )
}
